import React, {useEffect, useState} from "react";
import {FormattedMessage, useIntl} from "react-intl";
import * as Sentry from "@sentry/browser";

import Api from "../../Api";
import classes from "./styles.module.css";

/**
 * Что должно быть в файле реестра — по выбранному провайдеру.
 *
 * Описание колонок приходит с бэкенда (`/merchant/payout/registry/columns`),
 * оттуда же собирается xlsx-образец. Держать список здесь, на фронте, нельзя:
 * он неизбежно расходится с парсером — так в этой же странице жила проверка
 * документов под FinStar, хотя arMax документ получателя вовсе не смотрит.
 */
function RegistryColumnsHelp(props) {
    const intl = useIntl();
    const type = props.type === "sbp" ? "sbp" : "card";
    const provider = props.provider || null;

    const [open, setOpen] = useState(false);
    const [state, setState] = useState({loading: true, columns: [], notes: [], providerLabel: ""});

    useEffect(() => {
        // Пока терминал не выбран, показывать нечего: требования у банков
        // разные, а «средний» список только запутает — мерчант заполнит файл
        // не по своему провайдеру.
        if (!provider) {
            setState({loading: false, columns: [], notes: [], providerLabel: ""});

            return undefined;
        }

        let alive = true;
        setState((prev) => ({...prev, loading: true}));

        new Api({token: props.token})
            .getRegistryColumns(type, provider)
            .then((data) => {
                if (!alive) return;

                setState({
                    loading: false,
                    columns: (data && data.columns) || [],
                    notes: (data && data.notes) || [],
                    providerLabel: (data && data.providerLabel) || "",
                });
            })
            .catch((error) => {
                if (!alive) return;

                Sentry.captureException(error);
                setState({loading: false, columns: [], notes: [], providerLabel: ""});
            });

        return () => {
            alive = false;
        };
    }, [type, provider, props.token]);

    const required = state.columns.filter((column) => column.requirement === "required");
    const optional = state.columns.filter((column) => column.requirement !== "required");

    const templateUrl =
        (process.env.REACT_APP_API_URL || process.env.API_URL || "") +
        "/merchant/payout/registry/template?type=" +
        encodeURIComponent(type) +
        (provider ? "&provider=" + encodeURIComponent(provider) : "");

    function renderGroup(title, columns) {
        if (columns.length === 0) return null;

        return (
            <div className={classes.Group}>
                <div className={classes.GroupTitle}>{title}</div>
                <div className={classes.Columns}>
                    {columns.map((column) => (
                        <div key={column.name} className={classes.Column}>
                            <div className={classes.ColumnHead}>
                                <span className={classes.ColName}>{column.name}</span>
                                <span className={classes.Example}>{column.example}</span>
                            </div>
                            <div className={classes.ColumnText}>{column.description}</div>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    return (
        <div className={classes.Wrap}>
            <button type="button" className={classes.Toggle} onClick={() => setOpen(!open)} aria-expanded={open}>
                <span className={open ? classes.ChevronOpen : classes.Chevron}/>
                <span>
                    {state.providerLabel
                        ? intl.formatMessage(
                              {
                                  id: "RegistryColumnsHelp.titleProvider",
                                  defaultMessage: "Как заполнить файл для {provider}",
                              },
                              {provider: state.providerLabel}
                          )
                        : intl.formatMessage({
                              id: "RegistryColumnsHelp.title",
                              defaultMessage: "Как заполнить файл",
                          })}
                </span>
            </button>

            {open && (
                <div className={classes.Content}>
                    {!provider ? (
                        <div className={classes.Placeholder}>
                            <FormattedMessage
                                id="RegistryColumnsHelp.selectTerminal"
                                defaultMessage="Выберите проект и терминал — покажем, как заполнить файл именно для вашего провайдера."
                            />
                        </div>
                    ) : state.loading ? (
                        <div className={classes.Loading}>
                            <FormattedMessage id="RegistryColumnsHelp.loading" defaultMessage="Загружаем описание…"/>
                        </div>
                    ) : state.columns.length === 0 ? (
                        <div className={classes.Loading}>
                            <FormattedMessage
                                id="RegistryColumnsHelp.empty"
                                defaultMessage="Не удалось загрузить описание колонок."
                            />
                        </div>
                    ) : (
                        <React.Fragment>
                            {renderGroup(
                                intl.formatMessage({
                                    id: "RegistryColumnsHelp.group.required",
                                    defaultMessage: "Обязательные колонки",
                                }),
                                required
                            )}
                            {renderGroup(
                                intl.formatMessage({
                                    id: "RegistryColumnsHelp.group.optional",
                                    defaultMessage: "Необязательные колонки",
                                }),
                                optional
                            )}

                            {state.notes.length > 0 && (
                                <ul className={classes.Notes}>
                                    {state.notes.map((note, index) => (
                                        <li key={index}>{note}</li>
                                    ))}
                                </ul>
                            )}

                            <a className={classes.TemplateLink} href={templateUrl} download>
                                {state.providerLabel
                                    ? intl.formatMessage(
                                          {
                                              id: "RegistryColumnsHelp.templateProvider",
                                              defaultMessage: "Скачать образец файла для {provider}",
                                          },
                                          {provider: state.providerLabel}
                                      )
                                    : intl.formatMessage({
                                          id: "RegistryColumnsHelp.template",
                                          defaultMessage: "Скачать образец файла",
                                      })}
                            </a>
                        </React.Fragment>
                    )}
                </div>
            )}
        </div>
    );
}

export default RegistryColumnsHelp;



// WEBPACK FOOTER //
// ./src/Components/RegistryColumnsHelp/index.js